(ns manage.adset-decision-tree-page.view.table.tiktok
  (:require ["@mui/material/Icon$default" :as Icon]
            ["@mui/material/Switch$default" :as Switch]
            ["@mui/material/Tooltip$default" :as Tooltip]
            ["moment/min/moment-with-locales$default" :as moment]
            [cljs-bean.core :refer [->clj ->js]]
            [clojure.string]
            [common.queries :refer [use-mutation]]
            [common.resource.query.adset-activation
             :refer [update-activation-mutation]]
            [common.util :refer [->locale-string]]
            [common.widgets.data-grid :as dg]
            [manage.ad-variant.hooks :as ah]
            [manage.ad-variant.page.columns :as ad-variant-columns]
            [manage.adset-decision-tree-page.view.table-util :as tu]
            [uix.core :as uix :refer [$ defhook defui]]
            [uix.re-frame :refer [use-subscribe]]))

(defn get-cell-value [path _value row ^js _column ^js _apiRef]
  (get-in row path))

(defui enabled-cell [{{:keys [enabled id orphan]} :value}]
  (let [{update-activation :mutate} (-> (use-subscribe [:url-context])
                                        (assoc :activation-id id)
                                        update-activation-mutation
                                        use-mutation)]
    ($ Tooltip
       {:title "Switch on to run this ad on TikTok; switch off to pause it."
        :placement "top"}
       ($ Switch
          {:checked enabled
           :disabled orphan
           :onChange (fn [] (update-activation {:enabled (not enabled)}))
           :onClick #(.stopPropagation %)}))))

(defui live-now-cell [{live? :value}]
  ($ Icon {:sx #js {:fontWeight "bold"
                    :color (if live? "success.main" "grey.20")}}
     (if live? "checkmark" "remove")))

(defn get-integration-error-message [row]
  (let [data-map (get row :data)
        [{:keys [timestamp data]}] (get data-map :push-errors)
        last-push-data-timestamp (get data-map :last-push-data-timestamp)
        last-push-status-timestamp (get data-map :last-push-status-timestamp)
        last-push-data (get data-map :last-push-data)
        last-push-timestamp (or last-push-status-timestamp last-push-data-timestamp)
        error-after-latest-push? (or (nil? last-push-timestamp)
                                     (.isAfter (moment timestamp) last-push-timestamp))
        nothing-to-do-with-error? (and (= data-map last-push-data) (some? last-push-timestamp))
        error-message (get data :message)]
    (when (and error-after-latest-push? (not nothing-to-do-with-error?))
      error-message)))

(defn get-integration-feedback-value
  [_value row ^js _column ^js _apiRef]
  (get-integration-error-message row))

(defn get-columns [has-grouping]
  (->> [{:field "enabled"
         :headerName ""
         :width 70
         :minWidth 70
         :sortable false
         :valueGetter (fn [_value row]
                        (let [data-map (get row :data)]
                          (select-keys data-map [:orphan :enabled :id])))
         :renderCell (dg/get-cell-renderer enabled-cell)}
        (tu/get-activation-identifier-column has-grouping :group-id)
        (ad-variant-columns/get-ad-type-column {:show-scheduling true
                                                :data-path [:data]})
        (assoc tu/decision-path-column :valueGetter (fn [_ row]
                                                      (->> (get-in row [:data :label-path])
                                                           (remove clojure.string/blank?)
                                                           (clojure.string/join " / "))))
        {:field "tiktok-ad-id"
         :sortable false
         :headerName "TikTok Ad ID"
         :width 200
         :headerAlign "right"
         :align "right"
         :valueGetter (fn [_value row] (tu/get-ad-id "tiktok" row))}
        {:field "tiktok-ad-group-id"
         :sortable false
         :headerName "TikTok Ad Group ID"
         :width 200
         :headerAlign "right"
         :align "right"
         :valueGetter (fn [_value row] (tu/get-ad-group-id "tiktok" row))}
        {:field "integration-feedback"
         :sortable false
         :headerName "Integration feedback"
         :width 200
         :valueGetter get-integration-feedback-value}
        {:field "live-now"
         :headerName "Live Now"
         :width 100
         :valueGetter (partial get-cell-value [:data :live-now])
         :renderCell (dg/get-cell-renderer live-now-cell)}
        {:field "impressions"
         :filterable false
         :width 80
         :headerName "Impressions"
         :valueGetter (partial get-cell-value [:data :sync-data :metrics :impressions])
         :type "number"
         :renderCell (fn [^js params]
                       ((fnil ->locale-string 0) (.-value params)))}
        {:field "clicks"
         :filterable false
         :width 80
         :headerName "Clicks"
         :valueGetter (partial get-cell-value [:data :sync-data :metrics :clicks])
         :type "number"
         :renderCell (fn [^js params]
                       ((fnil ->locale-string 0) (.-value params)))}
        (assoc tu/activation-status-column
               :valueGetter (fn [_ row] (get-in row [:data :activation-status]))
               :renderCell tu/render-combined-status-cell)
        (assoc tu/targeting-preview-column :valueGetter
               (fn [_value row] #{(get-in row [:data :data 0 :ad-variant-id])}))
        (assoc tu/navigate-to-node-column :valueGetter (fn [_value row] (:data row)))]

       ;; Only render the identifier-column and preview columns on the groups
       (map (fn [col-def-js]
              (let [{:keys [field] :as col-def} (->clj col-def-js)]
                (cond-> (->clj col-def)
                  (and has-grouping
                       (not (contains? #{(:field tu/targeting-preview-column)
                                         (.-field ad-variant-columns/identifier-column)}
                                       field)))
                  (update :renderCell dg/render-only-child-rows)))))

       ;; These columns are needed for filters
       (concat [{:field "template"}
                {:field "ad-variant-id"}])
       ->js))

(defhook use-columns []
  (let [has-grouping (ah/use-has-multiple-dimensions)]
    (uix/use-memo (fn [] (get-columns has-grouping)) [has-grouping])))

(def field->sort-field
  {(.-field ad-variant-columns/identifier-column) "content-id>"})

(def field->resolver
  {"live-now" "live-now"
   "template" "template"
   (.-field ad-variant-columns/identifier-column) "content-id"
   (:field tu/activation-status-column) "activation-status"})

(def field->column
  {"live-now" "live-now"
   (.-field ad-variant-columns/identifier-column) "content-id"
   (:field tu/activation-status-column) "activation-status"
   "template" "template"})

(def query-filter-params
  {:field->sort-field field->sort-field
   :field->resolver field->resolver
   :field->column field->column
   :default-sorting {:field "content-id>" :sort "asc"}
   :quick-filter {:field (.-field ad-variant-columns/identifier-column)}})

(def aggregation
  (let [all-first-aggregation
        (reduce (fn [m ^js col-def] (assoc m (.-field col-def) "first"))
                {}
                (get-columns false))]
    (-> all-first-aggregation
        (assoc (:field tu/targeting-preview-column) "union")
        ->js)))

(defn get-row-id [row]
  (str (:group-id row) "-" (get-in row [:data :data 0 :ad-variant-id])))

(defn get-ad-variant-id [row] (get-in row [:data :data 0 :ad-variant-id]))
